import { LIA_SAFEGUARD_OPTIONS } from './lia.constants';
import type {
  Lia,
  LiaDTO,
  LiaSafeguardCode,
  SoftDeleteLiaInput,
  SoftDeleteLiaInputDTO,
  UpdateLiaInput,
  UpdateLiaInputDTO,
} from './lia.types';

const isLiaSafeguardCode = (value: unknown): value is LiaSafeguardCode =>
  typeof value === 'string' && (LIA_SAFEGUARD_OPTIONS as readonly string[]).includes(value);

export const parseSafeguardsCodesForFE = (value: unknown): LiaSafeguardCode[] => {
  if (Array.isArray(value)) {
    return value.filter(isLiaSafeguardCode);
  }

  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, entryValue]) => entryValue === true || entryValue === 1 || entryValue === 'true')
      .map(([code]) => code)
      .filter(isLiaSafeguardCode);
  }

  return [];
};

/** Schema: string. Some BE envs still return a context object — always normalize to a display string. */
export const parsePurposeContextForFE = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const context = value as Record<string, unknown>;
    if (typeof context.purpose_text === 'string' && context.purpose_text.trim()) {
      return context.purpose_text;
    }
    if (typeof context.processing_activity_name === 'string' && context.processing_activity_name.trim()) {
      return context.processing_activity_name;
    }
  }

  return '';
};

export const parseLiaForFE = (dto: LiaDTO): Lia => ({
  id: dto.id,
  revision: dto.revision ?? 1,
  validationStatus: dto.validation_status ?? 'invalid',
  isDeleted: Boolean(dto.is_deleted),
  deletedAt: dto.deleted_at ?? null,
  deletionReason: dto.deletion_reason ?? '',
  necessityAssessment: dto.necessity_assessment ?? '',
  balancingAssessment: dto.balancing_assessment ?? '',
  vulnerableDataSubjectsInvolved: Boolean(dto.vulnerable_data_subjects_involved),
  vulnerableDataSubjectsComment: dto.vulnerable_data_subjects_comment ?? '',
  safeguardsCodes: parseSafeguardsCodesForFE(dto.safeguards_codes),
  safeguardsDescription: dto.safeguards_description ?? '',
  rightToObjectHandling: dto.right_to_object_handling ?? '',
  conclusion: dto.conclusion ?? '',
  conclusionComment: dto.conclusion_comment ?? '',
  purposeContext: parsePurposeContextForFE(dto.purpose_context),
  updatedAt: dto.updated_at,
});

export const parseUpdateLiaForBE = (payload: UpdateLiaInput): UpdateLiaInputDTO => ({
  revision: payload.revision,
  necessity_assessment: payload.necessityAssessment.trim(),
  balancing_assessment: payload.balancingAssessment.trim(),
  vulnerable_data_subjects_involved: payload.vulnerableDataSubjectsInvolved,
  vulnerable_data_subjects_comment: payload.vulnerableDataSubjectsComment.trim(),
  safeguards_codes: payload.safeguardsCodes,
  safeguards_description: payload.safeguardsDescription.trim(),
  right_to_object_handling: payload.rightToObjectHandling.trim(),
  conclusion: payload.conclusion,
  conclusion_comment: payload.conclusionComment.trim(),
});

export const parseSoftDeleteLiaForBE = (payload: SoftDeleteLiaInput): SoftDeleteLiaInputDTO => ({
  deletion_reason: payload.deletionReason.trim(),
});

export const mapLiaToUpdateInput = (lia: Lia): UpdateLiaInput => ({
  revision: lia.revision,
  necessityAssessment: lia.necessityAssessment,
  balancingAssessment: lia.balancingAssessment,
  vulnerableDataSubjectsInvolved: lia.vulnerableDataSubjectsInvolved,
  vulnerableDataSubjectsComment: lia.vulnerableDataSubjectsComment,
  safeguardsCodes: lia.safeguardsCodes,
  safeguardsDescription: lia.safeguardsDescription,
  rightToObjectHandling: lia.rightToObjectHandling,
  conclusion: lia.conclusion,
  conclusionComment: lia.conclusionComment,
});
